import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { selectPendingPromptData } from '../store/selectors';
import { getImageById } from '../services/apiService';
import CollectionSelectModal from './CollectionSelectModal';
import ShareModal from './ShareModal';
import './ImageZoomOverlay.css';

// Helper function to get user indicator background style (dynamic)
const getUserIndicatorStyle = (user) => ({
    background: user ? `url('/user-profile.png') center center/cover no-repeat` : 'white',
});

// Helper function to get color dot style (dynamic)
const getColorDotStyle = (color) => ({
    background: color,
});

// Helper function to format field ID to readable label
const formatFieldLabel = (fieldId) => {
    return fieldId
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .replace(/^\w/, c => c.toUpperCase());
};

// Helper to check if value is an image file or URL
const isImageValue = (value) => {
    if (value instanceof File) return true;
    if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:'))) return true;
    return false;
};

// Helper to check if value looks like a color (hex or color name)
const isColorValue = (value) => {
    if (typeof value !== 'string') return false;
    if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(value)) return true;
    const colorNames = ['red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'black', 'white', 'gray', 'grey', 'brown', 'cyan', 'magenta'];
    return colorNames.includes(value.toLowerCase());
};


export default function ImageZoomOverlayMobile({ imageUrl, imageData, onClose, onImageUpdate, mode = 'result' }) {
    const userInputs = useSelector(selectPendingPromptData);
    const navigate = useNavigate();
    const [flipped, setFlipped] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [showCollectionModal, setShowCollectionModal] = useState(false);
    const [user, setUser] = useState(null);
    const [isBookmarked, setIsBookmarked] = useState(false);

    // Get all dynamic fields from userInputs (only for result mode)
    // Filter out empty values and special fields that we handle separately
    const specialFields = ['timestamp', 'assets', 'projectName', 'idea', 'fields', 'selectedTag', 'availableTags', 'dynamicInput'];
    const dynamicFieldsList = [];

    if (userInputs && mode === 'result') {
        // First, add dynamicInput fields (convert camelCase keys to title text)
        if (userInputs.dynamicInput && typeof userInputs.dynamicInput === 'object') {
            Object.entries(userInputs.dynamicInput).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '' &&
                    !(Array.isArray(value) && value.length === 0)) {
                    const titleKey = key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase())
                        .trim();
                    dynamicFieldsList.push([titleKey, value]);
                }
            });
        }

        // Then add other fields (excluding special fields and dynamicInput)
        Object.entries(userInputs).forEach(([key, value]) => {
            if (specialFields.includes(key)) return;
            if (value === null || value === undefined || value === '') return;
            if (Array.isArray(value) && value.length === 0) return;
            if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) return;
            dynamicFieldsList.push([key, value]);
        });
    }

    const dynamicFields = dynamicFieldsList;

    // Extract special fields for specific rendering (only for result mode)
    const assets = (mode === 'result' && userInputs?.assets) || [];
    const projectName = (mode === 'result' && userInputs?.projectName) || '';
    const idea = (mode === 'result' && userInputs?.idea) || '';

    useEffect(() => {
        // Only fetch user for result and collection modes
        if (mode !== 'share') {
            supabase.auth.getUser().then(({ data: { user } }) => {
                setUser(user);
            });
        }
    }, [mode]);

    // Use saved status from imageData if available, otherwise fetch it (only for result/collection modes)
    useEffect(() => {
        // Skip for share mode
        if (mode === 'share') {
            return;
        }

        // If imageData is provided (from result page), use it directly
        if (imageData?.saved !== undefined) {
            setIsBookmarked(imageData.saved === true);
            return;
        }

        // If opened via URL without imageData, fetch the image data
        const imageId = imageData?.id;
        if (imageId && user) {
            getImageById(imageId).then((response) => {
                if (response.success && response.data) {
                    setIsBookmarked(response.data.saved || false);
                }
            }).catch((error) => {
                console.error('Error fetching image data:', error);
                setIsBookmarked(false);
            });
        }
    }, [imageData?.saved, imageData?.id, user, mode]);

    const stopPropagation = (e) => e.stopPropagation();

    const handleShareClick = (e) => {
        e.stopPropagation();
        setShowSharePopup(true);
    };

    const handleTryItClick = () => {
        navigate('/');
    };

    return (
        <div className="image-zoom-overlay-mobile" onClick={onClose}>
            {/* Kura Logo - Top Left - Shared across all modes */}
            <div className="image-zoom-logo">
                <img src="/KURA-logo.png" alt="KURA Logo" style={{ height: '40px', width: 'auto' }} />
            </div>
            {/* Close Button - Hidden in share mode */}
            {mode !== 'share' && (
                <button
                    className="image-zoom-close-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    title="Close"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6L6 18M6 6L18 18" />
                    </svg>
                </button>
            )}

            {/* Share mode: Image with promotional content */}
            {mode === 'share' && (
                <div className="image-zoom-container-mobile-simplified" style={{ justifyContent: 'center', width: '100vw', height: '100vh', paddingTop: '60px' }} onClick={stopPropagation}>
                    <img
                        src={imageUrl}
                        alt="Shared"
                        className="image-zoom-main-image-simplified-mobile"
                        style={{ maxWidth: '95%', maxHeight: '65%', marginBottom: '20px' }}
                    />
                    {/* Promotional Content */}
                    <div style={{
                        textAlign: 'center',
                        color: 'white',
                        maxWidth: '90%',
                        padding: '0 20px'
                    }}>
                        <h2 style={{ fontSize: '1.2rem', marginBottom: '10px', fontWeight: 600 }}>
                            Create Your Own Amazing Images
                        </h2>
                        <p style={{ fontSize: '0.9rem', marginBottom: '20px', color: '#b0b3b8', lineHeight: '1.4' }}>
                            Transform your ideas into stunning visuals with AI-powered image generation. Try it yourself and see what you can create!
                        </p>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleTryItClick();
                            }}
                            style={{
                                background: '#72da58',
                                color: 'black',
                                border: 'none',
                                borderRadius: '24px',
                                padding: '14px 32px',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                        >
                            Try It Yourself →
                        </button>
                    </div>
                </div>
            )}

            {/* Collection mode: Just the image */}
            {mode === 'collection' && (
                <div className="image-zoom-container-mobile-simplified" style={{ justifyContent: 'center', width: '100vw', height: '100vh' }} onClick={stopPropagation}>
                    <img
                        src={imageUrl}
                        alt="Zoomed"
                        className="image-zoom-main-image-simplified-mobile"
                        onClick={onClose}
                    />
                </div>
            )}

            {/* Result mode: Flip card with prompt data */}
            {mode === 'result' && (
                <>
                    <div className="image-zoom-container-mobile" onClick={stopPropagation}>
                        <div className="image-zoom-image-box-mobile" onClick={() => setFlipped((f) => !f)}>
                            <div className={`image-zoom-flip-container ${flipped ? 'flipped' : ''}`}>
                                {/* Front side: image */}
                                <div className="image-zoom-flip-front image-zoom-flip-front-mobile">
                                    <img src={imageUrl} alt="Zoomed" className="image-zoom-main-image" />
                                </div>
                                {/* Back side: actual data */}
                                <div className="image-zoom-flip-back image-zoom-flip-back-mobile">
                                    <div className="image-zoom-prompt-container image-zoom-prompt-container-mobile">
                                        {/* Project Name */}
                                        {projectName && (
                                            <>
                                                <div className="image-zoom-field-row image-zoom-field-row-mobile image-zoom-project-name-row-mobile" style={{ flexWrap: 'wrap' }}>
                                                    My Kura project is <span className="image-zoom-italic image-zoom-italic-mobile">{projectName}</span>
                                                </div>
                                                <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                            </>
                                        )}

                                        {/* Assets */}
                                        {assets && assets.length > 0 && (
                                            <>
                                                <div className="image-zoom-field-row image-zoom-field-row-mobile image-zoom-project-name-row-mobile" style={{ position: 'relative', flexWrap: 'wrap' }}>
                                                    We have <span className="image-zoom-italic image-zoom-italic-mobile">[visual assets]</span>
                                                    {assets.slice(0, 2).map((asset, idx) => {
                                                        // Handle both File objects, URL strings, and objects with url property
                                                        let assetUrl = null;
                                                        if (asset instanceof File) {
                                                            assetUrl = URL.createObjectURL(asset);
                                                        } else if (typeof asset === 'string') {
                                                            assetUrl = asset;
                                                        } else if (typeof asset === 'object' && asset.url) {
                                                            assetUrl = asset.url;
                                                        }

                                                        if (!assetUrl) return null;

                                                        return (
                                                            <img
                                                                key={idx}
                                                                alt={`asset-${idx}`}
                                                                src={assetUrl}
                                                                className={`image-zoom-asset-img image-zoom-asset-img-mobile-${idx}`}
                                                                onError={(e) => {
                                                                    console.error('Failed to load asset image:', assetUrl);
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                            </>
                                        )}

                                        {/* Idea */}
                                        {idea && (
                                            <>
                                                <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ position: 'relative', flexWrap: 'wrap' }}>
                                                    We need <span className="image-zoom-italic image-zoom-italic-mobile">[{idea}]</span>
                                                </div>
                                                <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                            </>
                                        )}

                                        {/* Dynamic Fields from Details Page */}
                                        {dynamicFields.map(([fieldId, value], index) => {
                                            const label = fieldId.includes(' ') ? fieldId : formatFieldLabel(fieldId);

                                            // Handle array of images
                                            if (Array.isArray(value) && value.length > 0 && value.every(v => isImageValue(v))) {
                                                return (
                                                    <React.Fragment key={fieldId}>
                                                        <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ position: 'relative', flexWrap: 'wrap' }}>
                                                            {label}: <span className="image-zoom-italic image-zoom-italic-mobile">[{value.length} images]</span>
                                                            {value.slice(0, 2).map((img, idx) => (
                                                                <img
                                                                    key={idx}
                                                                    alt={`${fieldId}-${idx}`}
                                                                    src={img instanceof File ? URL.createObjectURL(img) : img}
                                                                    className="image-zoom-asset-img"
                                                                    style={{
                                                                        width: idx === 0 ? 40 : 38,
                                                                        height: idx === 0 ? 40 : 38,
                                                                        objectFit: 'cover',
                                                                        borderRadius: 4,
                                                                        position: 'absolute',
                                                                        right: idx === 0 ? 50 : 5,
                                                                        top: idx === 0 ? -8 : 12,
                                                                        boxShadow: '1.5px 2.4px 14px #213',
                                                                        transform: idx === 0 ? 'rotate(-7deg)' : 'rotate(11deg)'
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                        <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                                    </React.Fragment>
                                                );
                                            }

                                            // Handle array of colors
                                            if (Array.isArray(value) && value.length > 0 && value.every(v => isColorValue(v))) {
                                                return (
                                                    <React.Fragment key={fieldId}>
                                                        <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ flexWrap: 'wrap' }}>
                                                            {label}:
                                                            <span style={{ display: 'flex', gap: 6, marginLeft: 12 }}>
                                                                {value.map((color, idx) => (
                                                                    <span key={idx} className="image-zoom-color-dot image-zoom-color-dot-mobile" style={getColorDotStyle(color)} />
                                                                ))}
                                                            </span>
                                                        </div>
                                                        <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                                    </React.Fragment>
                                                );
                                            }

                                            // Handle array of strings
                                            if (Array.isArray(value) && value.length > 0) {
                                                return (
                                                    <React.Fragment key={fieldId}>
                                                        <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ flexWrap: 'wrap' }}>
                                                            {label}: <span className="image-zoom-italic image-zoom-italic-mobile">[{value.join(', ')}]</span>
                                                        </div>
                                                        <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                                    </React.Fragment>
                                                );
                                            }

                                            // Handle single color value
                                            if (isColorValue(value)) {
                                                return (
                                                    <React.Fragment key={fieldId}>
                                                        <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ flexWrap: 'wrap' }}>
                                                            {label}:
                                                            <span className="image-zoom-color-dot image-zoom-color-dot-mobile" style={{ ...getColorDotStyle(value), marginLeft: 12 }} />
                                                        </div>
                                                        <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                                    </React.Fragment>
                                                );
                                            }

                                            // Handle font field specially
                                            if (fieldId.toLowerCase().includes('font') || fieldId.toLowerCase().includes('typography')) {
                                                return (
                                                    <React.Fragment key={fieldId}>
                                                        <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ flexWrap: 'wrap' }}>
                                                            {label}: <span className="image-zoom-typography-field image-zoom-typography-field-mobile" style={{ fontFamily: value || 'inherit' }}>{value}</span>
                                                        </div>
                                                        <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                                    </React.Fragment>
                                                );
                                            }

                                            // Default: text value
                                            return (
                                                <React.Fragment key={fieldId}>
                                                    <div className="image-zoom-field-row image-zoom-field-row-mobile" style={{ flexWrap: 'wrap' }}>
                                                        {label}: <span className="image-zoom-italic image-zoom-italic-mobile">[{value}]</span>
                                                    </div>
                                                    <hr className="image-zoom-divider image-zoom-divider-mobile" />
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Info panel and action buttons */}
                    <div className="image-zoom-info-panel-mobile" onClick={stopPropagation}>
                        {/* User Info */}
                        <div className="image-zoom-user-info-row-mobile">
                            <div className="image-zoom-user-indicator image-zoom-user-indicator-mobile" style={getUserIndicatorStyle(user)} />
                            <span className="image-zoom-user-name-mobile">
                                {user?.user_metadata?.full_name || user?.user_metadata?.name || 'User'}
                            </span>
                            <div className="image-zoom-user-actions-inline-mobile">
                                <button
                                    className={`image-zoom-action-btn image-zoom-action-btn-mobile image-zoom-inline-btn-mobile ${isBookmarked ? 'image-zoom-action-btn-bookmarked' : ''}`}
                                    title={isBookmarked ? "Remove from collection" : "Save to collection"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowCollectionModal(true);
                                    }}
                                >
                                    {isBookmarked ? (
                                        <div className="image-zoom-bookmark-filled image-zoom-bookmark-filled-mobile">
                                            <div
                                                className="image-zoom-bookmark-mask"
                                                style={{
                                                    WebkitMask: `url(${process.env.PUBLIC_URL || ''}/bookmark-icon.png) no-repeat center / contain`,
                                                    mask: `url(${process.env.PUBLIC_URL || ''}/bookmark-icon.png) no-repeat center / contain`
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <img
                                            src="/bookmark-icon.png"
                                            alt="Bookmark"
                                            style={{ width: 16, height: 16 }}
                                        />
                                    )}
                                </button>
                                <button className="image-zoom-action-btn-mobile image-zoom-inline-btn-mobile image-zoom-share-inline-mobile" title="Share" onClick={handleShareClick}>
                                    <img src="/share-icon.png" alt="Share" style={{ width: 16, height: 16 }} />
                                </button>
                            </div>
                        </div>
                        {/* Ad Product Block (moved outside flip) */}
                        {imageData?.isAd && (
                            <div className="image-zoom-ad-product image-zoom-ad-product-mobile">
                                <div className="image-zoom-ad-product-title image-zoom-ad-product-title-mobile">
                                    {imageData.title || 'Sponsored Product'}
                                </div>
                                {imageData.link && (
                                    <a
                                        href={imageData.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="image-zoom-ad-product-link image-zoom-ad-product-link-mobile"
                                    >
                                        View on Amazon ↗
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                    </div>
                    {/* Modals */}
                    <ShareModal
                        isOpen={showSharePopup}
                        onClose={() => setShowSharePopup(false)}
                        isMobile={true}
                        imageId={imageData?.id}
                        imageUrl={imageUrl}
                    />
                    {showCollectionModal && (
                        <CollectionSelectModal
                            isOpen={showCollectionModal}
                            onClose={() => {
                                setShowCollectionModal(false);
                                // Refresh image data (including saved status) after closing modal
                                const imageId = imageData?.id;
                                if (imageId && user) {
                                    getImageById(imageId).then((response) => {
                                        if (response.success && response.data) {
                                            setIsBookmarked(response.data.saved || false);
                                            // Also refresh result page images if callback provided
                                            if (onImageUpdate) {
                                                onImageUpdate();
                                            }
                                        }
                                    }).catch((error) => {
                                        console.error('Error refreshing image data:', error);
                                    });
                                }
                            }}
                            imageId={imageData?.id || null}
                        />
                    )}
                </>
            )}
        </div>
    );
}

